import React, { useRef, useState, useEffect, useCallback } from "react";
import { Play, Pause, RotateCcw, RotateCw } from "lucide-react";

function fmt(t) {
  if (!t || isNaN(t) || !isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const AudioPlayer = ({ src, hasAudio, onDuration, preload = "metadata" }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
    setReady(false);
  }, [src]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      a.play().catch(() => {});
    }
  }, [playing]);

  const skip = (delta) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.min(Math.max(0, a.currentTime + delta), duration || a.duration || 0);
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a) return;
    const v = Number(e.target.value);
    a.currentTime = v;
    setCurrent(v);
  };

  const changeRate = () => {
    const rates = [1, 1.25, 1.5, 0.75];
    const next = rates[(rates.indexOf(rate) + 1) % rates.length];
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  return (
    <div
      data-testid="audio-player-container"
      className="bg-[#F4EFE6] rounded-2xl p-6 sm:p-7 border border-[#E5DCCF] w-full shadow-inner flex flex-col gap-5"
      style={{ "--player-bg": "#E2D7C7" }}
    >
      {hasAudio ? (
        <audio
          ref={audioRef}
          src={src}
          preload={preload}
          onLoadedMetadata={(e) => { setDuration(e.target.duration); setReady(true); onDuration && onDuration(e.target.duration); }}
          onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
      ) : null}

      <div className="flex items-center justify-center gap-6">
        <button
          data-testid="skip-back-button"
          onClick={() => skip(-10)}
          disabled={!hasAudio}
          className="text-[#6E5A4F] hover:text-[#3A2921] transition-colors disabled:opacity-30 flex flex-col items-center"
          aria-label="Voltar 10 segundos"
        >
          <RotateCcw className="w-6 h-6" strokeWidth={1.6} />
          <span className="text-[10px] mt-1 tracking-wide">10s</span>
        </button>

        <button
          data-testid="play-pause-button"
          onClick={toggle}
          disabled={!hasAudio}
          className="w-[68px] h-[68px] sm:w-20 sm:h-20 rounded-full bg-[#3A2921] text-[#FDFBF7] hover:bg-[#5C4638] flex items-center justify-center transition-all transform hover:scale-105 shadow-md border-2 border-[#C5A059]/40 disabled:opacity-40 disabled:hover:scale-100"
          aria-label={playing ? "Pausar" : "Reproduzir"}
        >
          {playing ? (
            <Pause className="w-8 h-8" fill="currentColor" strokeWidth={0} />
          ) : (
            <Play className="w-8 h-8 ml-1" fill="currentColor" strokeWidth={0} />
          )}
        </button>

        <button
          data-testid="skip-forward-button"
          onClick={() => skip(10)}
          disabled={!hasAudio}
          className="text-[#6E5A4F] hover:text-[#3A2921] transition-colors disabled:opacity-30 flex flex-col items-center"
          aria-label="Avançar 10 segundos"
        >
          <RotateCw className="w-6 h-6" strokeWidth={1.6} />
          <span className="text-[10px] mt-1 tracking-wide">10s</span>
        </button>
      </div>

      <div className="w-full">
        <input
          data-testid="audio-progress-bar"
          type="range"
          className="scrub w-full"
          min={0}
          max={duration || 0}
          step="0.1"
          value={current}
          onChange={onSeek}
          disabled={!hasAudio}
          style={{
            background: duration
              ? `linear-gradient(to right, #C5A059 ${(current / duration) * 100}%, #E2D7C7 ${(current / duration) * 100}%)`
              : "#E2D7C7",
          }}
          aria-label="Progresso do áudio"
        />
        <div className="flex justify-between mt-2 text-xs font-mono text-[#8C7565] tracking-wide">
          <span data-testid="audio-current-time">{fmt(current)}</span>
          <span data-testid="audio-duration">{fmt(duration)}</span>
        </div>
      </div>

      <div className="flex items-center justify-center">
        <button
          data-testid="audio-speed-select"
          onClick={changeRate}
          disabled={!hasAudio}
          className="text-xs font-sans-body font-semibold tracking-wide text-[#6E5A4F] border border-[#E5DCCF] rounded-full px-4 py-1.5 hover:border-[#C5A059] hover:text-[#3A2921] transition-colors disabled:opacity-30"
        >
          Velocidade {rate}x
        </button>
      </div>

      {!hasAudio && (
        <p data-testid="audio-pending-notice" className="text-center text-sm text-[#9C877A] italic font-serif-display">
          O áudio desta oração estará disponível em breve.
        </p>
      )}
    </div>
  );
};
